'use client'

import React, { createContext, useContext, useEffect, useState } from 'react'
import { useSession } from 'next-auth/react'
import { io, Socket } from 'socket.io-client'
import type { CustomerChatSession, CustomerChatMessage, CustomerChatConfig } from '@/types/customerChat'

interface CustomerChatSocketContextType {
  socket: Socket | null
  isConnected: boolean
  currentSession: CustomerChatSession | null
  config: CustomerChatConfig | null
  messages: CustomerChatMessage[]
  isLoading: boolean
  error: string | null
  startChat: (vendorId: string) => Promise<void>
  sendMessage: (message: string, attachment?: string) => Promise<void>
  setTyping: (isTyping: boolean) => void
  isAiTyping: boolean
  isChatExpanded: boolean
  setIsChatExpanded: (expanded: boolean) => void
  isChatOpen: boolean
  setIsChatOpen: (open: boolean) => void
}

const CustomerChatSocketContext = createContext<CustomerChatSocketContextType | undefined>(undefined)

export function CustomerChatSocketProvider({ children }: { children: React.ReactNode }) {
  const { data: session } = useSession()
  const [socket, setSocket] = useState<Socket | null>(null)
  const [isConnected, setIsConnected] = useState(false)
  const [currentSession, setCurrentSession] = useState<CustomerChatSession | null>(null)
  const [config, setConfig] = useState<CustomerChatConfig | null>(null)
  const [messages, setMessages] = useState<CustomerChatMessage[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [isAiTyping, setIsAiTyping] = useState(false)

  const [isChatExpanded, setIsChatExpandedState] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = sessionStorage.getItem('customerChatExpanded')
      return saved ? JSON.parse(saved) : false
    }
    return false
  })

  const [isChatOpen, setIsChatOpenState] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = sessionStorage.getItem('customerChatOpen')
      return saved ? JSON.parse(saved) : false
    }
    return false
  })

  const setIsChatExpanded = (expanded: boolean) => {
    setIsChatExpandedState(expanded)
    if (typeof window !== 'undefined') {
      sessionStorage.setItem('customerChatExpanded', JSON.stringify(expanded))
    }
  }

  const setIsChatOpen = (open: boolean) => {
    setIsChatOpenState(open)
    if (typeof window !== 'undefined') {
      sessionStorage.setItem('customerChatOpen', JSON.stringify(open))
    }
  }

  const accessToken = session?.user?.accessToken

  useEffect(() => {
    const backendUrl = process.env.NEXT_PUBLIC_BACKEND_URL || 'http://localhost:3001'

    const socketInstance = io(backendUrl, {
      path: '/customer-chat/',
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionAttempts: 5,
      withCredentials: true,
      auth: accessToken ? { token: accessToken } : {}
    })

    socketInstance.on('connect', () => {
      setIsConnected(true)
      setError(null)
    })

    socketInstance.on('connect_error', (err) => {
      console.error('Customer chat socket: Connection error', err.message)
      setIsConnected(false)
      setError(err.message)
    })

    socketInstance.on('disconnect', () => {
      setIsConnected(false)
    })

    socketInstance.on('customer-new-message', (message: CustomerChatMessage) => {
      setMessages((prev) => [...prev, message])
    })

    socketInstance.on('customer-typing', (data: { sessionId: string; isTyping: boolean; userType: 'user' | 'ai' }) => {
      if (data.userType === 'ai') {
        setIsAiTyping(data.isTyping)
      }
    })

    socketInstance.on('customer-closed', () => {
      setCurrentSession((prev) => prev ? { ...prev, status: 'closed' } : null)
    })

    setSocket(socketInstance)

    return () => {
      socketInstance.disconnect()
    }
  }, [accessToken])

  const startChat = async (vendorId: string) => {
    if (!socket) {
      setError('Socket not connected')
      return
    }

    setIsLoading(true)
    setError(null)

    const frontendSessionId = session?.user?.sessionId

    return new Promise<void>((resolve, reject) => {
      socket.emit('customer-start', { vendorId, frontendSessionId }, (response: any) => {
        setIsLoading(false)
        if (response.status === 200) {
          setCurrentSession(response.session)
          setConfig(response.config)
          setMessages(response.messages)
          resolve()
        } else {
          setError(response.error || 'Failed to start chat')
          reject(new Error(response.error || 'Failed to start chat'))
        }
      })
    })
  }

  const sendMessage = async (message: string, attachment?: string) => {
    if (!socket || !currentSession) {
      setError('No active chat session')
      return
    }

    setError(null)

    return new Promise<void>((resolve, reject) => {
      socket.emit('customer-message', {
        message,
        attachment
      }, (response: any) => {
        if (response.status === 200) {
          resolve()
        } else {
          setError(response.error || 'Failed to send message')
          reject(new Error(response.error || 'Failed to send message'))
        }
      })
    })
  }

  const setTyping = (isTyping: boolean) => {
    if (!socket || !currentSession) return

    socket.emit('customer-typing', {
      sessionId: currentSession.id,
      isTyping
    })
  }

  return (
    <CustomerChatSocketContext.Provider
      value={{
        socket,
        isConnected,
        currentSession,
        config,
        messages,
        isLoading,
        error,
        startChat,
        sendMessage,
        setTyping,
        isAiTyping,
        isChatExpanded,
        setIsChatExpanded,
        isChatOpen,
        setIsChatOpen
      }}
    >
      {children}
    </CustomerChatSocketContext.Provider>
  )
}

export function useCustomerChatSocket() {
  const context = useContext(CustomerChatSocketContext)
  if (context === undefined) {
    throw new Error('useCustomerChatSocket must be used within CustomerChatSocketProvider')
  }
  return context
}